import AddNotebookButton from "pages/notebooks/add-notebook-button";
import Button from "atoms/buttons/button";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import Icon from "atoms/icons/icon";
import React from "react";
import SidebarAddPageButton from "pages/notebooks/pages/sidebar-add-page-button";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import useToggleNotebookSidebar from "pages/notebooks/use-toggle-notebook-sidebar";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { t } from "utilities/localization-utils";
import { useHistory } from "react-router";
import NotebookPanel from "./notebook-panel";
import SharedPagePanel from "./shared-page-panel";
import PagePanel from "./page-panel";
import useNotebookSidebar from "./use-notebook-sidebar";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import { ButtonSizes } from "atoms/constants/button-sizes";
import useToggleNotebookReferencePanel from "../notebook-reference-panel/use-toggle-notebook-reference-panel";
import Loader from "atoms/loaders/loader";
import { LoaderStyles } from "atoms/constants/loader-styles";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { NotebookConstants } from "constants/notebook-constants";
import NotebookRecord from "models/view-models/notebook-record";

const BASE_CLASS_NAME = "c-notebook-sidebar";

const NotebookSidebar = () => {
    const userId = useGlobalState().globalState.currentIdentity?.userId();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const { showSidebar, toggleDisplay: toggleNotebookSidebar } =
        useToggleNotebookSidebar();
    const { showPanel, toggleDisplay: toggleReferencePanel } =
        useToggleNotebookReferencePanel();
    const { isMobile, isLaptop } = useNotebookBreakpoints();
    const { notebookRoute, getNotebookClassModifier } = useNotebookSidebar();
    const history = useHistory();
    const [openNotebookIds, setOpenNotebookIds] = React.useState<number[]>([]);
    const { resultObject: notebooks, loading } = useNotebooksAtom();

    const handleToggleNotebookSidebarClick = () => {
        if (showPanel && isLaptop) {
            toggleReferencePanel();
        }
        toggleNotebookSidebar();
    };
    const handleNotebookTitleClick = (id: number, isOpen: boolean) => {
        handleNotebookExpand(id, isOpen);
        navigateToNotebook(id);
    };

    const handleNotebookExpand = (id: number, isOpen: boolean) => {
        if (!isOpen) {
            setOpenNotebookIds((prev) => [...prev, id]);
        }
    };

    const navigateToNotebook = (notebookId: number) => {
        history.push(notebookRoute(notebookId)!);
        if (isMobile) {
            toggleNotebookSidebar();
        }
    };

    const handleNotebookClose = (id: number) => {
        setOpenNotebookIds((prev) => prev.filter((x) => x !== id));
    };

    const toggleSidebarButton = showSidebar ? "-open" : "-closed";
    const toggleSidebar = showSidebar ? "-show" : "-collapse";

    const isSharedNotebook = (notebook: NotebookRecord) => {
        return notebook.ownerId !== Number(userId);
    };
    const isSharedPagesNotebook = (notebook: NotebookRecord) => {
        return notebook.id === NotebookConstants.SharedPageNotebookId;
    };
    const noPages = (notebook: NotebookRecord) => {
        return notebook?.pages?.length === 0;
    };
    return (
        <>
            {!isMobile && (
                <Button
                    cssClassName={`${BASE_CLASS_NAME}__displayButton ${toggleSidebarButton}`}
                    onClick={handleToggleNotebookSidebarClick}
                    style={ButtonStyles.Tertiary}>
                    <Icon
                        type={
                            showSidebar ? Icons.ChevronLeft : Icons.ChevronRight
                        }
                    />
                </Button>
            )}
            {isMobile && (
                <div
                    className={`${BASE_CLASS_NAME}__mobileOverlay ${toggleSidebar}`}
                />
            )}
            <div className={`${BASE_CLASS_NAME} ${toggleSidebar}`}>
                <div className={`${BASE_CLASS_NAME}__header ${toggleSidebar}`}>
                    <div className={`${BASE_CLASS_NAME}__header__heading`}>
                        <Icon type={Icons.Notebook} size={IconSizes.Medium} />
                        {t("notebooks")}
                    </div>
                    {isMobile && (
                        <Button
                            accessibleText={t("close")}
                            cssClassName={"-modal-close -icon"}
                            onClick={handleToggleNotebookSidebarClick}
                            size={ButtonSizes.Medium}
                            style={ButtonStyles.TertiaryAlt}>
                            <Icon type={Icons.Close} size={IconSizes.Large} />
                        </Button>
                    )}
                </div>

                <div
                    className={`${BASE_CLASS_NAME}__itemContainer`}
                    ref={containerRef}>
                    {loading && <Loader type={LoaderStyles.LinkGlyphGray} />}
                    {notebooks.map((notebook) => {
                        const isOpen = openNotebookIds.includes(
                            Number(notebook.id)
                        );

                        return (
                            <div
                                className={`${BASE_CLASS_NAME}__item ${getNotebookClassModifier(notebook.id!)} ${isOpen ? "-open" : ""}`}
                                key={notebook.id}>
                                <CollapsePanel
                                    buttonAriaText={`toggle ${notebook.name}`}
                                    collapse={!isOpen}
                                    iconSize={IconSizes.Base}
                                    panelTop={
                                        <NotebookPanel notebook={notebook} />
                                    }
                                    onTitleClick={() =>
                                        handleNotebookTitleClick(
                                            Number(notebook.id),
                                            isOpen
                                        )
                                    }
                                    onOpen={() =>
                                        handleNotebookExpand(
                                            Number(notebook.id),
                                            isOpen
                                        )
                                    }
                                    onClose={() =>
                                        handleNotebookClose(Number(notebook.id))
                                    }>
                                    {notebook.pages?.map((page) =>
                                        isSharedPagesNotebook(notebook) ? (
                                            <SharedPagePanel
                                                key={`shared-${page.id}-${page.name}`}
                                                page={page}
                                                notebook={notebook}
                                            />
                                        ) : (
                                            <PagePanel
                                                key={`${page.id}-${page.name}`}
                                                page={page}
                                                notebookId={
                                                    isSharedNotebook(notebook)
                                                        ? NotebookConstants.SharedPageNotebookId
                                                        : Number(notebook.id)
                                                }
                                            />
                                        )
                                    )}
                                    <div
                                        className={`${BASE_CLASS_NAME}__addPageButton`}>
                                        {!isSharedPagesNotebook(notebook) &&
                                            !isSharedNotebook(notebook) && (
                                                <SidebarAddPageButton
                                                    notebookId={Number(
                                                        notebook.id
                                                    )}
                                                />
                                            )}
                                    </div>
                                    {isSharedPagesNotebook(notebook) &&
                                        noPages(notebook) && (
                                            <div
                                                className={`${BASE_CLASS_NAME}__sharedPage`}>
                                                <span>
                                                    {t(
                                                        "notebooks-shared-pages-placeholder"
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                </CollapsePanel>
                            </div>
                        );
                    })}

                    <div className={`${BASE_CLASS_NAME}__addNotebook`}>
                        <AddNotebookButton containerRef={containerRef} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotebookSidebar;
